import { PRO_MONTHLY_PRICE } from "../helpers/proPriceExperiment";
import {
  PLAN_TO_CLIP_COUNT_LIMIT,
  PLAN_TO_LABEL,
  PLAN_TO_PRICE,
  PlanType,
  UPLOAD_DURATION_ALLOWANCE_SECONDS,
} from "./planConst";
import { SITE_URL } from "./seo";

// The one source for every Klap price, plan name, plan limit and trial claim
// on marketing pages: the homepage FAQ, the /tools/* and /alternatives/*
// templates, the hero microcopy and their JSON-LD offers. /pricing renders
// from the same values (PricingCards reads MONTHLY_PRICE, CLIPS_PER_MONTH and
// YEARLY_DISCOUNT), so a plan change made in planConst / proPriceExperiment
// reaches both. Marketing pages show what a logged-out /pricing visitor
// sees: Pro's control price (the pricing-pro-59 arm only applies to new
// signups) and the clip-count limits.

export type PublicPlanKey = "basic" | "pro" | "pro-plus";

// Yearly plans are billed at this discount off the monthly price.
export const YEARLY_DISCOUNT = 0.5;

// Per-month price shown for a discounted plan ("$14/mo billed yearly").
export const calculateDiscountedPrice = (
  originalPrice: number,
  discountPercentage: number
): number => {
  return Math.floor(originalPrice * (1 - discountPercentage));
};

const requiredPrice = (plan: PlanType): number => {
  const price = PLAN_TO_PRICE.get(plan);
  if (price === undefined) throw new Error(`No price for plan ${plan}`);
  return price;
};

// Month-to-month prices.
export const MONTHLY_PRICE: Record<PublicPlanKey, number> = {
  basic: requiredPrice("basic"),
  pro: PRO_MONTHLY_PRICE.control,
  "pro-plus": requiredPrice("pro-plus"),
};

export const CLIPS_PER_MONTH: Record<PublicPlanKey, number> = {
  basic: PLAN_TO_CLIP_COUNT_LIMIT.get("basic") ?? 0,
  pro: PLAN_TO_CLIP_COUNT_LIMIT.get("pro") ?? 0,
  "pro-plus": PLAN_TO_CLIP_COUNT_LIMIT.get("pro-plus") ?? 0,
};

export type PublicPlan = {
  key: PublicPlanKey;
  name: string;
  monthlyPrice: number;
  // Per month, billed yearly.
  yearlyMonthlyPrice: number;
  // Charged once a year.
  yearlyPrice: number;
  clipsPerMonth: number;
};

const publicPlan = (key: PublicPlanKey): PublicPlan => ({
  key,
  name: PLAN_TO_LABEL.get(key) ?? key,
  monthlyPrice: MONTHLY_PRICE[key],
  yearlyMonthlyPrice: calculateDiscountedPrice(
    MONTHLY_PRICE[key],
    YEARLY_DISCOUNT
  ),
  yearlyPrice: MONTHLY_PRICE[key] * 12 * (1 - YEARLY_DISCOUNT),
  clipsPerMonth: CLIPS_PER_MONTH[key],
});

export const PUBLIC_PLANS: PublicPlan[] = [
  publicPlan("basic"),
  publicPlan("pro"),
  publicPlan("pro-plus"),
];

const [BASIC, PRO, PRO_PLUS] = PUBLIC_PLANS;

export const PRICING_URL = `${SITE_URL}/pricing`;

const usd = (amount: number) => `$${amount}`;
const clips = (count: number) => `${count.toLocaleString("en-US")} clips`;

// What a new account can do (klap-api profile defaults: video_count_limit 1,
// export_count_limit 0; posting needs a paid plan): turn one video into clips
// and preview or edit them. There is no free plan, and the trial can't
// download or post, so no copy may promise free or watermark-free exports.
export const FREE_TRIAL_VIDEOS = 1;

// Microcopy under every hero and final-CTA video input.
export const TRIAL_CTA = `Try Klap free on ${FREE_TRIAL_VIDEOS} video. No credit card required.`;

export const TRIAL_SUMMARY = `You can try Klap free on ${FREE_TRIAL_VIDEOS} video with no credit card: create a free account and Klap turns that video into clips you can preview and edit.`;

export const EXPORTS_NEED_PLAN =
  "Downloading and posting clips needs a paid plan. Paid exports carry no Klap branding.";

export const STARTING_PRICE = `Paid plans start at ${usd(BASIC.yearlyMonthlyPrice)}/month billed yearly (${usd(BASIC.monthlyPrice)} month to month).`;

// "Try Klap free with no credit card. Paid plans from $14/mo billed yearly."
export const TRIAL_AND_STARTING_PRICE_SHORT = `Try Klap free on ${FREE_TRIAL_VIDEOS} video with no credit card. Paid plans from ${usd(BASIC.yearlyMonthlyPrice)}/mo billed yearly.`;

export const PLANS_SUMMARY = `Paid plans: ${BASIC.name} is ${usd(BASIC.yearlyMonthlyPrice)}/month for ${clips(BASIC.clipsPerMonth)} a month, ${PRO.name} ${usd(PRO.yearlyMonthlyPrice)}/month for ${clips(PRO.clipsPerMonth)} and ${PRO_PLUS.name} ${usd(PRO_PLUS.yearlyMonthlyPrice)}/month for ${clips(PRO_PLUS.clipsPerMonth)}, all billed yearly (${usd(BASIC.monthlyPrice)}, ${usd(PRO.monthlyPrice)} and ${usd(PRO_PLUS.monthlyPrice)} month to month).`;

// Longest source video any paid plan accepts.
export const MAX_UPLOAD_HOURS = UPLOAD_DURATION_ALLOWANCE_SECONDS / 3600;

export const ENTRY_PLAN_SUMMARY = `Klap ${BASIC.name} is ${usd(BASIC.yearlyMonthlyPrice)}/month billed yearly (${usd(BASIC.monthlyPrice)} month to month) for ${clips(BASIC.clipsPerMonth)} a month`;

// Klap's cell in the alternatives comparison tables' price rows.
export const PRICE_COMPARE_CELL = `From ${usd(BASIC.yearlyMonthlyPrice)}/mo (${clips(BASIC.clipsPerMonth)})`;

// Klap's cell in the alternatives comparison tables' free-trial rows.
export const TRIAL_COMPARE_CELL = `${FREE_TRIAL_VIDEOS} video, no card`;

// Shared by the homepage FAQ, the alternatives FAQ and their FAQPage JSON-LD.
export const IS_KLAP_FREE_FAQ = {
  question: "Is Klap free?",
  answerText: `${TRIAL_SUMMARY} ${EXPORTS_NEED_PLAN} ${PLANS_SUMMARY}`,
};

// Answer for the tool pages' "Can I … for free?" FAQs.
export const freeTrialAnswer = (toolName: string): string =>
  `You can try Klap's ${toolName} free on ${FREE_TRIAL_VIDEOS} video with no credit card: create a free account and Klap turns that video into clips you can preview and edit. ${EXPORTS_NEED_PLAN} ${STARTING_PRICE}`;

// Answer for the tool pages' "Does Klap add a watermark?" FAQs.
export const WATERMARK_ANSWER = `Not on paid plans: clips you download or post on a paid plan carry no Klap branding. The free trial turns ${FREE_TRIAL_VIDEOS} video into clips you can preview and edit in Klap, and downloading or posting them needs a paid plan. ${STARTING_PRICE}`;

export type OfferJsonLd = {
  "@type": "Offer";
  name: string;
  description: string;
  price: string;
  priceCurrency: "USD";
  url: string;
  priceValidUntil: string;
  priceSpecification: {
    "@type": "UnitPriceSpecification";
    price: string;
    priceCurrency: "USD";
    billingDuration: "P1M" | "P1Y";
  };
};

// SoftwareApplication `offers`: every paid plan, billed monthly and yearly,
// at the prices /pricing charges. The free trial isn't an offer of its own.
// Prices aren't scheduled to change, so each build (the pages are static)
// states them valid for a year.
export const buildKlapOffersJsonLd = (now: Date = new Date()): OfferJsonLd[] => {
  const validUntil = new Date(now);
  validUntil.setUTCFullYear(validUntil.getUTCFullYear() + 1);
  const priceValidUntil = validUntil.toISOString().slice(0, 10);
  return PUBLIC_PLANS.flatMap((plan) =>
    (
      [
        ["monthly", plan.monthlyPrice, "P1M"],
        ["yearly", plan.yearlyPrice, "P1Y"],
      ] as const
    ).map(([interval, price, billingDuration]) => ({
      "@type": "Offer",
      name: `Klap ${plan.name}, billed ${interval}`,
      description: `${clips(plan.clipsPerMonth)} per month`,
      price: String(price),
      priceCurrency: "USD",
      url: PRICING_URL,
      priceValidUntil,
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: String(price),
        priceCurrency: "USD",
        billingDuration,
      },
    }))
  );
};
